import * as React from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  PageHeader,
  Table,
  Button,
  Spin,
  Alert,
  Tag,
  Modal,
  InputNumber,
  Space,
  Descriptions,
  Empty,
  message as antdMessage,
  Radio,
  Checkbox,
  List,
} from "antd";
import {
  CheckCircleOutlined,
  CameraOutlined,
  EnvironmentOutlined,
  InfoCircleOutlined,
  ArrowRightOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  InventoryClient,
  PositionStockEntryDto,
  ProductStockEntryDto,
  MoveRequestDto,
  MoveItemDto,
} from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage } from "../utils/apiError";
import { useBarcodeScanner, BarcodeScanResult } from "../hooks/useBarcodeScanner";
import { useKeyboardScanner } from "../hooks/useKeyboardScanner";

interface RouteParams {
  positionId: string;
}

interface SelectedProduct {
  product: PositionStockEntryDto;
  quantityToMove: number;
  targetPosition: string;
}

// LocalStorage key prefix for temporary position sessions
const STORAGE_KEY_PREFIX = "temporary-position-";

// Minimal product data for localStorage (avoid storing full product objects)
interface StoredProduct {
  SkuId: number;
  ProductCode: string;
  ProductName: string;
  BatchNumber: string;
  Quantity: number;
  Unit: string;
}

interface StoredSelectedProduct {
  product: StoredProduct;
  quantityToMove: number;
  targetPosition: string;
}

interface StoredSession {
  positionId: string;
  batchMode: boolean;
  batchTargetPosition: string;
  batchQueue: StoredSelectedProduct[];
  selectedProduct: StoredSelectedProduct | null;
  scannedTargetPosition: string;
}

// Create API client at module level for better testability
const inventoryClient = new InventoryClient(new ApiConfig());

const TemporaryPositionDetail: React.FC = () => {
  const { positionId } = useParams<RouteParams>();
  const history = useHistory();

  // Data state
  const [products, setProducts] = React.useState<PositionStockEntryDto[]>([]);
  const [productLocations, setProductLocations] = React.useState<Map<number, ProductStockEntryDto[]>>(new Map());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>("");

  // Selection state
  const [selectedProduct, setSelectedProduct] = React.useState<SelectedProduct | null>(null);
  const [scannedTargetPosition, setScannedTargetPosition] = React.useState<string>("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Batch mode state
  const [batchMode, setBatchMode] = React.useState(false);
  const [batchTargetPosition, setBatchTargetPosition] = React.useState<string>("");
  const [batchQueue, setBatchQueue] = React.useState<SelectedProduct[]>([]);
  const [batchError, setBatchError] = React.useState<string>("");

  // Feedback
  const [lastFeedback, setLastFeedback] = React.useState<string>("");
  const [feedbackType, setFeedbackType] = React.useState<"success" | "error" | "info">("info");

  // Audio refs
  const successAudioRef = React.useRef<HTMLAudioElement>(null);
  const errorAudioRef = React.useRef<HTMLAudioElement>(null);

  // Flag to prevent saving during initial load
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  // Storage key for this specific position
  const storageKey = React.useMemo(
    () => `${STORAGE_KEY_PREFIX}${positionId}`,
    [positionId]
  );

  // Helper to convert PositionStockEntryDto to StoredProduct
  const toStoredProduct = (product: PositionStockEntryDto): StoredProduct => ({
    SkuId: product.SkuId,
    ProductCode: product.ProductCode,
    ProductName: product.ProductName,
    BatchNumber: product.BatchNumber,
    Quantity: product.Quantity ?? 0,
    Unit: product.Unit,
  });

  // Helper to find matching product from loaded products
  const findMatchingProduct = (storedProduct: StoredProduct): PositionStockEntryDto | undefined => {
    return products.find(
      p => p.SkuId === storedProduct.SkuId && p.BatchNumber === storedProduct.BatchNumber
    );
  };

  // Load products on mount
  React.useEffect(() => {
    loadPositionProducts();
  }, [positionId]);

  // Load from localStorage after products are loaded
  React.useEffect(() => {
    if (loading || products.length === 0) {
      return;
    }

    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) {
        setIsInitialLoad(false);
        return;
      }

      const session: StoredSession = JSON.parse(saved);

      // Restore batch mode
      setBatchMode(session.batchMode);
      setBatchTargetPosition(session.batchTargetPosition);

      // Restore batch queue
      const restoredQueue: SelectedProduct[] = [];
      for (const storedItem of session.batchQueue) {
        const matchingProduct = findMatchingProduct(storedItem.product);
        if (matchingProduct) {
          restoredQueue.push({
            product: matchingProduct,
            quantityToMove: storedItem.quantityToMove,
            targetPosition: storedItem.targetPosition,
          });
        }
      }
      setBatchQueue(restoredQueue);

      // Restore selected product
      if (session.selectedProduct) {
        const matchingProduct = findMatchingProduct(session.selectedProduct.product);
        if (matchingProduct) {
          setSelectedProduct({
            product: matchingProduct,
            quantityToMove: session.selectedProduct.quantityToMove,
            targetPosition: session.selectedProduct.targetPosition,
          });
        }
      }

      // Restore scanned target position
      setScannedTargetPosition(session.scannedTargetPosition);

    } catch (error) {
      console.error("Failed to load session from localStorage:", error);
    } finally {
      setIsInitialLoad(false);
    }
  }, [loading, products, storageKey]);

  // Auto-save to localStorage when state changes
  React.useEffect(() => {
    if (isInitialLoad) {
      return;
    }

    try {
      // Check if there's anything to save
      const hasData = batchMode || batchQueue.length > 0 || selectedProduct !== null || scannedTargetPosition !== "";

      if (!hasData) {
        // Nothing to save, clear storage
        localStorage.removeItem(storageKey);
        return;
      }

      const session: StoredSession = {
        positionId,
        batchMode,
        batchTargetPosition,
        batchQueue: batchQueue.map(item => ({
          product: toStoredProduct(item.product),
          quantityToMove: item.quantityToMove,
          targetPosition: item.targetPosition,
        })),
        selectedProduct: selectedProduct ? {
          product: toStoredProduct(selectedProduct.product),
          quantityToMove: selectedProduct.quantityToMove,
          targetPosition: selectedProduct.targetPosition,
        } : null,
        scannedTargetPosition,
      };

      localStorage.setItem(storageKey, JSON.stringify(session));
    } catch (error) {
      console.error("Failed to save session to localStorage:", error);
    }
  }, [
    isInitialLoad,
    positionId,
    batchMode,
    batchTargetPosition,
    batchQueue,
    selectedProduct,
    scannedTargetPosition,
    storageKey,
  ]);

  const loadPositionProducts = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await inventoryClient.getPositionProducts(positionId);
      setProducts(data);

      // Load locations for all products
      const locationsMap = new Map<number, ProductStockEntryDto[]>();
      for (const product of data) {
        try {
          const locations = await inventoryClient.getProductStockEntries(product.SkuId);
          locationsMap.set(product.SkuId, locations);
        } catch (err) {
          console.error(`Failed to load locations for SKU ${product.SkuId}:`, err);
        }
      }
      setProductLocations(locationsMap);
    } catch (err) {
      console.error("Failed to load position products:", err);
      setError(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Play feedback sound
  const playSound = (type: "success" | "error") => {
    if (type === "success" && successAudioRef.current) {
      successAudioRef.current.play().catch(() => {});
    } else if (type === "error" && errorAudioRef.current) {
      errorAudioRef.current.play().catch(() => {});
    }
  };

  // Show feedback message
  const showFeedback = (msg: string, type: "success" | "error" | "info") => {
    setLastFeedback(msg);
    setFeedbackType(type);
    playSound(type === "success" ? "success" : "error");

    setTimeout(() => {
      setLastFeedback("");
    }, 3000);
  };

  // Check if code is a position marker
  const isPositionCode = (code: string): boolean => {
    const trimmed = code.trim();
    return trimmed.endsWith("@");
  };

  // Extract position from code
  const extractPosition = (code: string): string => {
    return code.trim().replace(/@$/, "").toUpperCase();
  };

  // Parse product barcode
  const parseBarcode = (barcode: string): { productCode: string; batchNumber: string } | null => {
    const parts = barcode.split("#");

    if (parts.length >= 2) {
      return {
        productCode: parts[0],
        batchNumber: parts[1],
      };
    } else if (parts.length === 1 && barcode.length > 0) {
      return { productCode: barcode, batchNumber: "" };
    }

    return null;
  };

  // Handle scanned code
  const processScannedCode = async (code: string) => {
    if (!code) return;

    // Check for position marker
    if (isPositionCode(code)) {
      const position = extractPosition(code);
      if (!position) {
        showFeedback("Neplatná pozícia!", "error");
        return;
      }

      if (position === positionId) {
        showFeedback("Cieľová pozícia nemôže byť rovnaká ako zdrojová!", "error");
        return;
      }

      // Batch mode: set batch target position
      if (batchMode) {
        setBatchTargetPosition(position);
        showFeedback(`Dávková cieľová pozícia: ${position}`, "success");
        return;
      }

      // Quick mode: need selected product
      if (!selectedProduct) {
        showFeedback("Najprv vyberte produkt na presun!", "error");
        return;
      }

      setScannedTargetPosition(position);
      showFeedback(`Cieľová pozícia: ${position}`, "success");
      return;
    }

    // Parse product barcode
    const parsed = parseBarcode(code);
    if (!parsed) {
      showFeedback("Neplatný formát kódu produktu!", "error");
      return;
    }

    // Find matching product in the list
    const matchingProduct = products.find(
      (p) => p.ProductCode === parsed.productCode && p.BatchNumber === parsed.batchNumber
    );

    if (!matchingProduct) {
      showFeedback(`Produkt ${parsed.productCode} nie je na tejto pozícii!`, "error");
      return;
    }

    // Select this product
    setSelectedProduct({
      product: matchingProduct,
      quantityToMove: matchingProduct.Quantity ?? 0,
      targetPosition: "",
    });
    setScannedTargetPosition("");
    showFeedback(`Vybraný produkt: ${matchingProduct.ProductCode}`, "success");
  };

  // Camera barcode scanner hook
  const handleCameraScan = React.useCallback(
    async (result: BarcodeScanResult) => {
      await processScannedCode(result.rawValue);
    },
    [selectedProduct, products]
  );

  const { cameraStatus, startCamera, stopCamera, scanBarcode, videoRef, selectRef, error: cameraError } =
    useBarcodeScanner(handleCameraScan);

  // Keyboard barcode scanner hook (for HID scanners)
  useKeyboardScanner(processScannedCode);

  // Handle product selection from table
  const handleSelectProduct = (product: PositionStockEntryDto) => {
    setSelectedProduct({
      product,
      quantityToMove: product.Quantity ?? 0,
      targetPosition: "",
    });
    setScannedTargetPosition("");
  };

  // Handle quantity change
  const handleQuantityChange = (value: number | null) => {
    const maxQuantity = selectedProduct?.product.Quantity ?? 0;
    if (selectedProduct && value && value > 0 && value <= maxQuantity) {
      setSelectedProduct({
        ...selectedProduct,
        quantityToMove: value,
      });
    }
  };

  // Submit move
  const handleSubmitMove = async () => {
    if (!selectedProduct) {
      antdMessage.error("Vyberte produkt!");
      return;
    }

    if (!scannedTargetPosition) {
      antdMessage.error("Naskenujte cieľovú pozíciu!");
      return;
    }

    setIsSubmitting(true);

    try {
      const moveRequest: MoveRequestDto = {
        FromStoragePositionId: positionId,
        ToStoragePositionId: scannedTargetPosition,
        AccountingDocumentNumber: null,
        Items: [
          {
            SkuId: selectedProduct.product.SkuId,
            SkuCode: selectedProduct.product.ProductCode,
            BatchNumber: selectedProduct.product.BatchNumber,
            Quantity: selectedProduct.quantityToMove,
            BatchExpirationDate: null,
          },
        ],
      };

      await inventoryClient.move(moveRequest);

      antdMessage.success(
        `Produkt ${selectedProduct.product.ProductCode} presunutý na ${scannedTargetPosition}!`
      );

      // Reload products
      await loadPositionProducts();

      // Reset selection
      setSelectedProduct(null);
      setScannedTargetPosition("");

      // Clear localStorage after successful move
      localStorage.removeItem(storageKey);

      showFeedback("Presun dokončený!", "success");
    } catch (err) {
      console.error("Move failed:", err);
      const errorMsg = getApiErrorMessage(err);
      antdMessage.error(`Chyba pri presune: ${errorMsg}`);
      showFeedback(`Chyba: ${errorMsg}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel selection
  const handleCancelSelection = () => {
    setSelectedProduct(null);
    setScannedTargetPosition("");
  };

  // Batch mode functions
  const addToBatch = (product: PositionStockEntryDto) => {
    // Check if already in batch
    const existingIndex = batchQueue.findIndex(item =>
      item.product.SkuId === product.SkuId &&
      item.product.BatchNumber === product.BatchNumber
    );

    if (existingIndex >= 0) {
      antdMessage.warning(`${product.ProductCode} už je v dávke`);
      return;
    }

    setBatchQueue(prev => [...prev, {
      product,
      quantityToMove: product.Quantity ?? 0,
      targetPosition: batchTargetPosition
    }]);

    showFeedback(`Pridané do dávky: ${product.ProductCode}`, "success");
  };

  const removeFromBatch = (skuId: number, batchNumber: string) => {
    setBatchQueue(prev => prev.filter(item =>
      !(item.product.SkuId === skuId && item.product.BatchNumber === batchNumber)
    ));
  };

  const toggleProductInBatch = (product: PositionStockEntryDto) => {
    const isInBatch = batchQueue.some(item =>
      item.product.SkuId === product.SkuId &&
      item.product.BatchNumber === product.BatchNumber
    );

    if (isInBatch) {
      removeFromBatch(product.SkuId, product.BatchNumber);
    } else {
      addToBatch(product);
    }
  };

  const addAllToBatch = () => {
    const newItems = products
      .filter(product => !batchQueue.some(item =>
        item.product.SkuId === product.SkuId &&
        item.product.BatchNumber === product.BatchNumber
      ))
      .map(product => ({
        product,
        quantityToMove: product.Quantity ?? 0,
        targetPosition: batchTargetPosition
      }));

    setBatchQueue(prev => [...prev, ...newItems]);
    antdMessage.success(`Pridaných ${newItems.length} produktov do dávky`);
  };

  const clearBatch = () => {
    setBatchQueue([]);
    setBatchError("");
    antdMessage.info("Dávka vyčistená");
  };

  // Update quantity for batch item
  const updateBatchQuantity = (skuId: number, batchNumber: string, newQuantity: number) => {
    setBatchQueue(prev => prev.map(item => {
      if (item.product.SkuId === skuId && item.product.BatchNumber === batchNumber) {
        return { ...item, quantityToMove: newQuantity };
      }
      return item;
    }));
  };

  // Execute batch move
  const executeBatchMove = async () => {
    if (batchQueue.length === 0) {
      const error = "Dávka je prázdna!";
      setBatchError(error);
      antdMessage.error(error);
      return;
    }

    if (!batchTargetPosition) {
      const error = "Nie je nastavená cieľová pozícia!";
      setBatchError(error);
      antdMessage.error(error);
      return;
    }

    setIsSubmitting(true);
    setBatchError("");

    try {
      const moveRequest: MoveRequestDto = {
        FromStoragePositionId: positionId,
        ToStoragePositionId: batchTargetPosition,
        AccountingDocumentNumber: null,
        Items: batchQueue.map(item => ({
          SkuId: item.product.SkuId,
          SkuCode: item.product.ProductCode,
          BatchNumber: item.product.BatchNumber,
          Quantity: item.quantityToMove,
          BatchExpirationDate: null,
        })),
      };

      await inventoryClient.move(moveRequest);

      const itemCount = batchQueue.length;
      antdMessage.success(
        `Dávkový presun dokončený: ${itemCount} ${itemCount === 1 ? 'produkt' : itemCount < 5 ? 'produkty' : 'produktov'} → ${batchTargetPosition}`
      );

      // Clear batch
      setBatchQueue([]);
      setBatchTargetPosition("");

      // Reload products
      await loadPositionProducts();

      // Clear localStorage after successful batch move
      localStorage.removeItem(storageKey);

      showFeedback("Dávkový presun úspešne dokončený!", "success");
    } catch (err) {
      console.error("Batch move failed:", err);
      const errorMsg = getApiErrorMessage(err);
      setBatchError(errorMsg);
      antdMessage.error(`Chyba pri dávkovom presune: ${errorMsg}`);
      showFeedback(`Chyba: ${errorMsg}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Product table columns
  const columns = [
    // Checkbox column (only in batch mode)
    ...(batchMode ? [{
      title: (
        <Checkbox
          onChange={(e) => {
            if (e.target.checked) addAllToBatch();
            else clearBatch();
          }}
          checked={batchQueue.length > 0 && batchQueue.length === products.length}
          indeterminate={batchQueue.length > 0 && batchQueue.length < products.length}
        >
          Všetky
        </Checkbox>
      ),
      key: "checkbox",
      width: 80,
      render: (_: any, product: PositionStockEntryDto) => {
        const isInBatch = batchQueue.some(item =>
          item.product.SkuId === product.SkuId &&
          item.product.BatchNumber === product.BatchNumber
        );
        return (
          <Checkbox
            checked={isInBatch}
            onChange={() => toggleProductInBatch(product)}
            disabled={isSubmitting}
          />
        );
      },
    }] : []),
    {
      title: "Produkt",
      key: "product",
      render: (product: PositionStockEntryDto) => {
        const locations = productLocations.get(product.SkuId) || [];
        // Filter out current position and show other locations
        const otherLocations = locations.filter(loc => loc.PositionId !== positionId);

        return (
          <div>
            <div>
              <strong>{product.ProductCode}</strong>
              {otherLocations.length > 0 && (
                <span style={{ marginLeft: 8 }}>
                  {otherLocations.map((loc, idx) => (
                    <Tag
                      key={idx}
                      color={loc.IsTemporary ? "orange" : "blue"}
                      style={{ marginLeft: 4, fontSize: 11 }}
                    >
                      {loc.PositionId}
                    </Tag>
                  ))}
                </span>
              )}
            </div>
            <span style={{ color: "#666", fontSize: 12 }}>{product.ProductName}</span>
          </div>
        );
      },
    },
    {
      title: "Šarža",
      dataIndex: "BatchNumber",
      key: "batchNumber",
      render: (text: string) => text || "-",
    },
    {
      title: "Množstvo",
      key: "quantity",
      render: (product: PositionStockEntryDto) =>
        product.Quantity !== null ? `${product.Quantity} ${product.Unit || ""}` : "-",
    },
    // Action button column (only in quick mode)
    ...(batchMode ? [] : [{
      title: "",
      key: "action",
      width: 120,
      render: (_: any, product: PositionStockEntryDto) => (
        <Button
          type={selectedProduct?.product.SkuId === product.SkuId ? "primary" : "default"}
          onClick={() => handleSelectProduct(product)}
          disabled={isSubmitting}
        >
          {selectedProduct?.product.SkuId === product.SkuId ? "Vybraný" : "Vybrať"}
        </Button>
      ),
    }]),
  ];

  return (
    <div style={{ padding: "0 0 80px 0", paddingBottom: cameraStatus !== 'idle' ? 'calc(25vh + 140px)' : '140px' }}>
      <PageHeader
        className="site-page-header"
        onBack={() => history.goBack()}
        title={`Zaraďovanie: ${positionId}`}
      />

      {/* Error Alert */}
      {error && (
        <Alert
          message="Chyba"
          description={error}
          type="error"
          closable
          onClose={() => setError("")}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Feedback Bar */}
      {lastFeedback && (
        <Alert
          message={lastFeedback}
          type={feedbackType}
          closable
          onClose={() => setLastFeedback("")}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {/* Mode Toggle */}
      <div style={{ marginBottom: 16 }}>
        <Radio.Group
          value={batchMode ? "batch" : "quick"}
          onChange={(e) => {
            const newMode = e.target.value === "batch";
            setBatchMode(newMode);
            if (!newMode) {
              // Switching to quick mode - clear batch
              setBatchQueue([]);
              setBatchTargetPosition("");
            } else {
              // Switching to batch mode - clear single selection
              setSelectedProduct(null);
              setScannedTargetPosition("");
            }
          }}
          buttonStyle="solid"
          style={{ marginBottom: 16 }}
        >
          <Radio.Button value="quick">Rýchly režim (po jednom)</Radio.Button>
          <Radio.Button value="batch">Dávkový režim (viacero naraz)</Radio.Button>
        </Radio.Group>
      </div>

      {/* Instructions */}
      <Alert
        message={batchMode ? "Dávkový režim" : "Rýchly režim"}
        description={
          batchMode ? (
            <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Naskenujte <strong>cieľovú pozíciu</strong> (kam majú ísť produkty)</li>
              <li>Zaškrtnite všetky produkty, ktoré majú ísť na túto pozíciu</li>
              <li>Kliknite <strong>Vykonať dávkový presun</strong></li>
            </ol>
          ) : (
            <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Vyberte alebo naskenujte produkt z tabuľky</li>
              <li>Naskenujte cieľovú pozíciu (kde má byť produkt uložený)</li>
              <li>Skontrolujte množstvo a potvrďte presun</li>
            </ol>
          )
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Batch Target Position (only in batch mode) */}
      {batchMode && (
        <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Cieľová pozícia">
            {batchTargetPosition ? (
              <Tag color="blue" style={{ fontSize: 16, padding: "4px 12px" }}>
                {batchTargetPosition}
              </Tag>
            ) : (
              <span style={{ color: "#999" }}>Naskenujte cieľovú pozíciu (@)</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Produktov v dávke">
            <strong style={{ fontSize: 16, color: "#1890ff" }}>
              {batchQueue.length}
            </strong>
          </Descriptions.Item>
        </Descriptions>
      )}

      {/* Batch Error Display */}
      {batchMode && batchError && (
        <Alert
          message="Chyba"
          description={batchError}
          type="error"
          closable
          onClose={() => setBatchError("")}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {/* Batch Queue Display */}
      {batchMode && batchQueue.length > 0 && (
        <Alert
          message={`Dávka → ${batchTargetPosition} (${batchQueue.length} ${batchQueue.length === 1 ? "produkt" : batchQueue.length < 5 ? "produkty" : "produktov"})`}
          description={
            <>
              <List
                size="small"
                dataSource={batchQueue}
                renderItem={(item, index) => (
                  <List.Item
                    actions={[
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeFromBatch(item.product.SkuId, item.product.BatchNumber)}
                        disabled={isSubmitting}
                      >
                        Odstrániť
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={`${index + 1}. ${item.product.ProductCode}`}
                      description={
                        <Space>
                          <span>Šarža: {item.product.BatchNumber || "-"}</span>
                          <span>|</span>
                          <span>Množstvo:</span>
                          <InputNumber
                            min={0.01}
                            max={item.product.Quantity ?? undefined}
                            value={item.quantityToMove}
                            onChange={(value) => {
                              const maxQuantity = item.product.Quantity ?? Infinity;
                              if (value && value > 0 && value <= maxQuantity) {
                                updateBatchQuantity(item.product.SkuId, item.product.BatchNumber, value);
                              }
                            }}
                            step={0.01}
                            precision={2}
                            disabled={isSubmitting}
                            size="small"
                            style={{ width: 100 }}
                          />
                          <span>{item.product.Unit || ""}</span>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
              <div style={{ marginTop: 16, textAlign: "center" }}>
                <strong>Celkové množstvo: {batchQueue.reduce((sum, item) => sum + item.quantityToMove, 0).toFixed(2)}</strong>
                <Button size="small" onClick={clearBatch} disabled={isSubmitting} style={{ marginLeft: 16 }}>
                  Vyčistiť dávku
                </Button>
              </div>
            </>
          }
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Products Table */}
      <Spin spinning={loading}>
        <Table
          dataSource={products}
          columns={columns}
          rowKey={(record) => `${record.SkuId}-${record.BatchNumber}`}
          pagination={false}
          scroll={{ x: true }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Žiadne produkty na tejto pozícii"
              />
            ),
          }}
          style={{ marginBottom: 16 }}
        />
      </Spin>

      {/* Selected Product Details */}
      {selectedProduct && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong style={{ fontSize: 16 }}>Vybraný produkt na presun</strong>
            <Button onClick={handleCancelSelection} disabled={isSubmitting}>
              Zrušiť výber
            </Button>
          </div>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Produkt">
              <strong>{selectedProduct.product.ProductCode}</strong> - {selectedProduct.product.ProductName}
            </Descriptions.Item>
            <Descriptions.Item label="Šarža">
              {selectedProduct.product.BatchNumber || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Dostupné množstvo">
              {selectedProduct.product.Quantity !== null
                ? `${selectedProduct.product.Quantity} ${selectedProduct.product.Unit}`
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Množstvo na presun">
              <InputNumber
                min={0.01}
                max={selectedProduct.product.Quantity ?? undefined}
                value={selectedProduct.quantityToMove}
                onChange={handleQuantityChange}
                step={0.01}
                precision={2}
                disabled={isSubmitting}
                style={{ width: 150 }}
              />{" "}
              {selectedProduct.product.Unit}
            </Descriptions.Item>
          </Descriptions>

          {/* Show existing locations */}
          <div style={{ marginTop: 16 }}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              <InfoCircleOutlined /> Produkt sa nachádza na pozíciách (
              {productLocations.get(selectedProduct.product.SkuId)?.length || 0})
            </div>
            {productLocations.has(selectedProduct.product.SkuId) && productLocations.get(selectedProduct.product.SkuId)!.length > 0 ? (
              <Table
                dataSource={productLocations.get(selectedProduct.product.SkuId)}
                columns={[
                  {
                    title: "Pozícia",
                    dataIndex: "PositionId",
                    key: "positionId",
                    render: (pos: string, record: ProductStockEntryDto) => (
                      <span>
                        {pos}{" "}
                        {record.IsTemporary && (
                          <Tag color="orange">Dočasná</Tag>
                        )}
                      </span>
                    ),
                  },
                  ...((productLocations.get(selectedProduct.product.SkuId)?.some((loc) => loc.BatchNumber)) ? [{
                    title: "Šarža",
                    dataIndex: "BatchNumber",
                    key: "batchNumber",
                    render: (text: string) => text || "-",
                  }] : []),
                  {
                    title: "Množstvo",
                    dataIndex: "Quantity",
                    key: "quantity",
                    render: (qty: number | null) =>
                      qty !== null ? `${qty} ${selectedProduct.product.Unit || ""}` : "-",
                  },
                ]}
                rowKey={(record) => `${record.PositionId}-${record.BatchNumber}`}
                pagination={false}
                size="small"
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Produkt sa nenachádza na žiadnej pozícii"
              />
            )}
          </div>
        </div>
      )}

      {/* Fixed bottom panel */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #f0f0f0",
          padding: "16px",
          zIndex: 100,
        }}
      >
        <Space style={{ width: "100%" }} direction="vertical">
          {/* Target Position Display */}
          {selectedProduct && (
            <Descriptions column={1} size="small" bordered style={{ marginBottom: 8 }}>
              <Descriptions.Item
                label={
                  <>
                    <EnvironmentOutlined /> Cieľová pozícia
                  </>
                }
              >
                {scannedTargetPosition ? (
                  <Tag color="green" style={{ fontSize: 16, padding: "4px 12px" }}>
                    {scannedTargetPosition}
                  </Tag>
                ) : (
                  <span style={{ color: "#999" }}>Naskenujte cieľovú pozíciu</span>
                )}
              </Descriptions.Item>
            </Descriptions>
          )}

          {/* Camera Section */}
          {cameraStatus !== "idle" && (
            <div>
              <select ref={selectRef} style={{ width: "100%", marginBottom: 8 }} />
              <div
                style={{
                  overflow: "hidden",
                  height: "20vh",
                  marginBottom: 8,
                  borderRadius: 4,
                }}
              >
                <video
                  ref={videoRef}
                  style={{
                    width: "100%",
                    position: "relative",
                    top: "50%",
                    transform: "translate(0, -50%)",
                  }}
                  muted
                  autoPlay
                  playsInline
                />
              </div>
            </div>
          )}

          {cameraError && (
            <Alert
              message={cameraError}
              type="error"
              closable
              onClose={() => stopCamera()}
              style={{ marginBottom: 8 }}
            />
          )}

          {/* Camera Controls */}
          {cameraStatus === "ready" ? (
            <Space style={{ width: "100%" }}>
              <Button
                size="large"
                onClick={scanBarcode}
                type="primary"
                icon={<CameraOutlined />}
                style={{ flex: 1 }}
              >
                Prečítať kód
              </Button>
              <Button size="large" onClick={stopCamera}>
                Zatvoriť kameru
              </Button>
            </Space>
          ) : (
            <Button
              size="large"
              onClick={() => {
                startCamera().catch((err) => {
                  console.error("Camera start failed:", err);
                });
              }}
              icon={<CameraOutlined />}
              loading={cameraStatus === "initializing"}
              block
            >
              {cameraStatus === "initializing" ? "Spúšťa sa..." : "Otvoriť kameru"}
            </Button>
          )}

          {/* Submit Buttons - Different for each mode */}
          {batchMode ? (
            // Batch mode button
            <Button
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              onClick={executeBatchMove}
              loading={isSubmitting}
              disabled={batchQueue.length === 0 || !batchTargetPosition}
              block
            >
              {isSubmitting
                ? "Presúvam..."
                : `Vykonať dávkový presun (${batchQueue.length} ${batchQueue.length === 1 ? "produkt" : batchQueue.length < 5 ? "produkty" : "produktov"})`}
            </Button>
          ) : (
            // Quick mode button
            <Button
              type="primary"
              size="large"
              icon={<CheckCircleOutlined />}
              onClick={handleSubmitMove}
              loading={isSubmitting}
              disabled={!selectedProduct || !scannedTargetPosition}
              block
            >
              Potvrdiť presun
            </Button>
          )}
        </Space>
      </div>

      {/* Audio elements */}
      <audio ref={successAudioRef}>
        <source src="/sounds/success.wav" />
      </audio>
      <audio ref={errorAudioRef}>
        <source src="/sounds/bad.mp3" />
      </audio>
    </div>
  );
};

export default TemporaryPositionDetail;
